import { AvatarGroup, ButtonBase, styled, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { formatAuthorNames } from './format-author-names';
import { REPLY_ROW_PADDING_X } from './reply-row';

const SummaryButton = styled(ButtonBase, { name: 'ResolvedSummary' })(({ theme }) => ({
  justifyContent: 'flex-start',
  gap: theme.spacing(1.25),
  width: '100%',
  padding: theme.spacing(1.25, REPLY_ROW_PADDING_X),
  borderTop: `1px solid ${theme.palette.divider}`,
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  textAlign: 'left',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.custom.OV.sys.onBackground,
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
}));

const Faces = styled(AvatarGroup, { name: 'ResolvedSummaryFaces' })(({ theme }) => ({
  '& .MuiAvatar-root': {
    borderColor: theme.palette.custom.OV.custom.InternalNotes.noteMessageContainer,
  },
}));

export interface ResolvedSummaryProps {
  /** Replies folded away by the resolution. */
  count: number;
  /** Their distinct authors, in order of first reply. */
  authorNames: string[];
  avatars: ReactNode[];
  expanded: boolean;
  onToggle: () => void;
}

/** The one line a resolved thread folds its earlier replies into. */
export const ResolvedSummary = ({
  count,
  authorNames,
  avatars,
  expanded,
  onToggle,
}: ResolvedSummaryProps) => (
  <SummaryButton onClick={onToggle} aria-expanded={expanded} data-testid="resolved-summary">
    <Faces max={3} spacing="medium">
      {avatars}
    </Faces>
    <Typography component="span" flex={1} minWidth={0} fontSize="inherit">
      {count} {count === 1 ? 'comment' : 'comments'} from {formatAuthorNames(authorNames)}
    </Typography>
    <DynamicIcon library="lucide" name={expanded ? 'chevron-up' : 'chevron-down'} />
  </SummaryButton>
);
